import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';

import type { RootState } from '@shared/store/store';

import {
  apiService,
  ConversationLogDto,
  ConversationLogListPaginatedControllerConversationLogListPaginatedApiResponse,
} from './apiService';
import { appendLogToPages } from './conversationLogs.utils';

export type ConversationLogsPage =
  ConversationLogListPaginatedControllerConversationLogListPaginatedApiResponse & {
    data?: ConversationLogDto[];
  };
export type ConversationLogsArg = { conversationId: string };
export type ConversationLogsEndpoint =
  | 'conversationLogsPaginated'
  | 'adminConversationLogsPaginated';
type CursorPageParam = string | null;

const MESSAGES_LIMIT_PER_REQUEST = 20;

const infiniteQueryOptions = {
  initialPageParam: null,
  getNextPageParam: (lastPage: ConversationLogsPage) =>
    lastPage.meta?.hasMore ? lastPage.meta.cursor : undefined,
};

const toParams = ({ conversationId }: ConversationLogsArg, pageParam: CursorPageParam) => ({
  conversationId,
  limit: MESSAGES_LIMIT_PER_REQUEST,
  cursor: pageParam ?? undefined,
});

// The cache keeps every page of a conversation, so live messages are appended with updateQueryData
// and a refetch after a reconnect reloads the whole history.
export const conversationLogsApi = apiService.injectEndpoints({
  endpoints: (build) => ({
    conversationLogsPaginated: build.infiniteQuery<
      ConversationLogsPage,
      ConversationLogsArg,
      CursorPageParam
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/v1/conversation-logs`,
        params: toParams(queryArg, pageParam),
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ConversationLogs', id: arg.conversationId },
      ],
    }),
    adminConversationLogsPaginated: build.infiniteQuery<
      ConversationLogsPage,
      ConversationLogsArg,
      CursorPageParam
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/v1/admin/conversation-logs`,
        params: toParams(queryArg, pageParam),
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ConversationLogs', id: arg.conversationId },
      ],
    }),
  }),
  overrideExisting: false,
});

/** Appends a live message to the cached history */
export const pushConversationLog =
  (
    endpoint: ConversationLogsEndpoint,
    arg: ConversationLogsArg,
    log: ConversationLogDto,
  ): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch) => {
    dispatch(
      conversationLogsApi.util.updateQueryData(endpoint, arg, (draft) =>
        appendLogToPages(draft, log),
      ),
    );
  };

export const {
  useConversationLogsPaginatedInfiniteQuery,
  useAdminConversationLogsPaginatedInfiniteQuery,
} = conversationLogsApi;
